import React, { useState, useEffect, Fragment, useRef } from 'react';
import { Menu } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSidebar } from '../../context/SidebarContext';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/auth';
import {
  MoonIcon, SunIcon, UserCircleIcon,
  CogIcon, LogoutIcon, InformationCircleIcon,
  HomeIcon, XIcon, ArrowsExpandIcon
} from '@heroicons/react/outline';

function InfoModal({ isOpen, setIsOpen, color = 'green' }) {
  const [displayedText, setDisplayedText] = useState('');
  const [loadingText, setLoadingText] = useState('');
  const [showIntro, setShowIntro] = useState(true);
  const [currentStep, setCurrentStep] = useState(-1);
  const [progress, setProgress] = useState(0);

  const steps = [
    {
      loading: 'جاري فحص النظام',
      content: 'جاري فحص النظام'
    },
    {
      loading: 'جاري فحص الإصدار',
      content: 'الإصدار: \n•الإصدار: 1.2.0\n'
    },
    {
      loading: 'جاري فحص التغييرات',
      content: 'التغييرات:\n• إضافة الوضع الليلي\n• تعديل بعض المشاكل التقنية\n• زيادة الحماية وتسريع بنية الموقع\n• تعديلات تقنية وبرمجية\n'
    },
    {
      loading: 'جاري فحص التطوير',
      content: 'المطور:\n• شركة STG ماجد امين\n'
    },
    {
      loading: 'جاري فحص الترخيص',
      content: 'الترخيص ممنوح لـ:\n•منصة دوز التعليمية \n•( أروى النفيعي )\n'
    }
  ];

  // تعريف الألوان المتاحة
  const colors = {
    green: {
      text: 'text-green-400',
      border: 'border-green-500/20',
      hover: 'hover:text-green-400',
      dim: 'text-green-500/60'
    },
    white: {
      text: 'text-white',
      border: 'border-white/20',
      hover: 'hover:text-white',
      dim: 'text-white/60'
    }
  };

  const currentColor = colors[color] || colors.green;

  useEffect(() => {
    let isMounted = true;

    if (!isOpen) {
      setDisplayedText('');
      setLoadingText('');
      setShowIntro(true);
      setCurrentStep(-1);
      setProgress(0);
      return;
    }

    const typeText = async (text, addDelay = true) => {
      const lines = text.split('\n');
      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex];
        // كتابة كل حرف في السطر
        for (let i = 0; i < line.length; i++) {
          if (!isMounted) return;
          await new Promise(resolve => setTimeout(resolve, 100));
          setDisplayedText(prev => prev + line[i]);
        }
        
        // إضافة سطر جديد إذا لم يكن السطر الأخير
        if (lineIndex < lines.length - 1) {
          setDisplayedText(prev => prev + '\n');
          // إضافة تأخير بين الأسطر
          if (addDelay) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
    };

    const animateProgress = async () => {
      // كتابة النص الأساسي حرفاً حرفاً
      const baseText = '     جاري فحص النظام ';
      for (let i = 0; i < baseText.length; i++) {
        if (!isMounted) return;
        await new Promise(resolve => setTimeout(resolve, 100));
        setDisplayedText(prev => prev + baseText[i]);
      }
      
      // انتظار لحظة قبل بدء النسبة
      await new Promise(resolve => setTimeout(resolve, 500));

      // عرض النسب من 0 إلى 100
      for (let i = 0; i <= 100; i += 2) {
        if (!isMounted) return;
        await new Promise(resolve => setTimeout(resolve, 50));
        setDisplayedText(baseText + `${i}%`);
      }
      
      // إضافة سطر جديد بعد اكتمال النسبة
      await new Promise(resolve => setTimeout(resolve, 500));
      setDisplayedText(prev => prev + '\n');
    };

    const runAnalysis = async () => {
      if (!isMounted) return;

      // عرض المقدمة أولاً
      if (showIntro) {
        await typeText('أنا مساعدك الشخصي كاسيد سوف اقوم باعلامك بكامل التفاصيل برجاء الانتظار\n', false);
        if (!isMounted) return;
        
        await new Promise(resolve => setTimeout(resolve, 2000));
        setDisplayedText('');
        setShowIntro(false);
        setCurrentStep(0);
        return;
      }

      // معالجة باقي الخطوات
      if (currentStep >= 0 && currentStep < steps.length) {
        const step = steps[currentStep];
        
        // عرض رسالة التحميل
        setLoadingText(step.loading);
        
        // انتظار قبل بدء الكتابة
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (!isMounted) return;
        
        // كتابة المحتوى حرفاً حرفاً
        if (currentStep === 0) {
          await animateProgress();
        } else {
          await typeText(step.content, true);
        }
        
        if (!isMounted) return;
        
        // انتظار قبل الخطوة التالية
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (!isMounted) return;
        
        if (currentStep < steps.length - 1) {
          setLoadingText(steps[currentStep + 1].loading);
          setCurrentStep(prev => prev + 1);
        } else {
          setLoadingText('');
        }
      }
    };

    runAnalysis();

    return () => {
      isMounted = false;
    };
  }, [isOpen, currentStep, showIntro]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center">
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" 
          onClick={() => setIsOpen(false)} 
        />

        <div className="relative w-full max-w-md p-4">
          <div className={`bg-gray-900 rounded-lg shadow-xl border ${currentColor.border}`}>
            <div className="p-4">
              <div className={`flex items-center justify-between mb-4 pb-2 border-b ${currentColor.border}`}>
                <h3 className={`text-lg ${currentColor.text}`}>
                  معلومات النظام
                </h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className={`${currentColor.dim} ${currentColor.hover} transition-colors`}
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              <div className={`font-mono ${currentColor.text}`}>
                {/* النص المعروض مع رسالة التحميل */}
                <div className="whitespace-pre-line">
                  {showIntro ? (
                    <div className="text-center my-6">
                      {displayedText}
                    </div>
                  ) : (
                    <div>
                      {displayedText}
                    </div>
                  )}
                  {loadingText && (
                    <div className="flex items-center space-x-2 space-x-reverse mt-4">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{loadingText}</span>
                      <span className="animate-pulse">...</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Navbar() {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [user, setUser] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const storedUser = sessionStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    // تم إزالة التوجيه هنا والاعتماد على التوجيه المركزي في auth.js
  };

  const userMenuItems = [
    { icon: HomeIcon, text: 'الصفحة الرئيسية', path: '/' },
    { icon: UserCircleIcon, text: 'الملف الشخصي', path: '/profile' },
    { icon: LogoutIcon, text: 'تسجيل الخروج', onClick: handleLogout }
  ];

  const handleMouseLeave = () => {
    setIsUserMenuOpen(false);
  };

  return (
    <>
      <nav className={`fixed top-0 ${isCollapsed ? 'left-0 right-16' : 'left-0 right-64'} h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10 transition-all duration-300`}>
        <div className="h-full px-4 flex items-center justify-between">
          {/* الجانب الأيسر */}
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">لوحة التحكم</h1>
          </div>

          {/* الجانب الأيمن - معلومات المستخدم */}
          <div className="flex items-center space-x-2">
            {/* زر تكبير الشاشة */}
            <button
              onClick={toggleFullscreen}
              className="p-2 ml-2"
            >
              <ArrowsExpandIcon className="h-6 w-6 text-green-500" />
            </button>

            {/* زر الوضع الليلي */}
            <button
              onClick={toggleTheme}
              className="p-2 ml-2"
            >
              {theme === 'dark' ? (
                <SunIcon className="h-6 w-6 text-yellow-400 transition-colors duration-200" />
              ) : (
                <MoonIcon className="h-6 w-6 text-indigo-500 dark:text-indigo-400 transition-colors duration-200" />
              )}
            </button>

            {/* أيقونة المعلومات */}
            <button
              onClick={() => setIsInfoModalOpen(true)}
              className="p-2 ml-2"
            >
              <InformationCircleIcon className="h-6 w-6 text-blue-500" />
            </button>

            {/* قائمة المستخدم */}
            <div className="relative" ref={menuRef}>
              <button
                className="flex items-center p-2 text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-gray-200"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                onMouseEnter={() => setIsUserMenuOpen(true)}
                onBlur={() => {
                  // تأخير بسيط عشان يسمح للقائمة تاخد الفوكس
                  setTimeout(() => setIsUserMenuOpen(false), 200);
                }}
                tabIndex={0}
              >
                {user?.avatar ? (
                  <div className="relative h-8 w-8 ml-2">
                    <img 
                      src={`/images/avatars/${user.avatar}.png`} 
                      alt="صورة المستخدم" 
                      className="h-8 w-8 rounded-full object-cover"
                      onError={(e) => {
                        // في حالة فشل تحميل الصورة، نعرض الأيقونة الافتراضية
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'block';
                      }}
                    />
                    <UserCircleIcon 
                      className="h-8 w-8 text-gray-600 dark:text-white absolute inset-0" 
                      style={{ display: 'none' }} 
                    />
                  </div>
                ) : (
                  <UserCircleIcon className="h-8 w-8 text-gray-600 dark:text-white ml-2" />
                )}
                <span className="text-sm font-medium">{user?.name || 'المستخدم'}</span>
              </button>

              <AnimatePresence>
                {isUserMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none"
                    onMouseLeave={() => setIsUserMenuOpen(false)}
                    onBlur={() => setIsUserMenuOpen(false)}
                    tabIndex={0}
                  >
                    {userMenuItems.map((item, index) => (
                      <div key={index}>
                        {item.onClick ? (
                          <button
                            onClick={() => {
                              item.onClick();
                              setIsUserMenuOpen(false);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-right text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 hover:cursor-pointer"
                          >
                            <item.icon className="ml-3 h-5 w-5 text-gray-500 dark:text-white" />
                            {item.text}
                          </button>
                        ) : (
                          <Link
                            to={item.path}
                            onClick={() => setIsUserMenuOpen(false)}
                            className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <item.icon className="ml-3 h-5 w-5 text-gray-500 dark:text-white" />
                            {item.text}
                          </Link>
                        )}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </nav>


      <InfoModal isOpen={isInfoModalOpen} setIsOpen={setIsInfoModalOpen} color="white" />
    </>
  );
}

export default Navbar;
